import {
  ArrowLeft,
  BookOpen,
  BookPlus,
  Check,
  ChevronDown,
  CircleAlert,
  Database,
  Gauge,
  GraduationCap,
  History,
  Landmark,
  LayoutDashboard,
  LibraryBig,
  LogOut,
  Search,
  ScrollText,
  Settings2,
  ShieldCheck,
  Star,
  UserRound,
  UsersRound,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, useLocation, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useId, useMemo, useRef, useState, type ComponentType } from "react";
import { apiFetch } from "@/lib/api";
import {
  enabledSupportedSubjects,
  isSupportedSubject,
  readLastSubject,
  requiredSubjectCapability,
  saveLastSubject,
  subjectAvailability,
  subjectQueryState,
  type SupportedSubjectId,
} from "@/lib/subjects";
import { useAuth, type User } from "./auth-context";
import { Button, EmptyState, LoadingLine } from "./primitives";

interface NavItem {
  label: string;
  to: string;
  icon: ComponentType<{ strokeWidth?: number }>;
  activePaths?: string[];
}

const englishLearningNav: NavItem[] = [
  { label: "学习概览", to: "/app/english/dashboard", icon: LayoutDashboard },
  { label: "快速查词", to: "/app/english/search", icon: Search },
  { label: "阅读生词", to: "/app/english/my-words", icon: BookPlus, activePaths: ["/app/english/my-words", "/app/english/capture"] },
  { label: "词库管理", to: "/app/english/word-library", icon: LibraryBig },
  { label: "背诵计划", to: "/app/english/review", icon: BookOpen },
];

const politicsLearningNav: NavItem[] = [
  { label: "学习概览", to: "/app/politics/dashboard", icon: LayoutDashboard },
  { label: "政治题库", to: "/app/politics/question-bank", icon: LibraryBig, activePaths: ["/app/politics/question-bank", "/app/politics/practice"] },
  { label: "错题本", to: "/app/politics/wrong-questions", icon: CircleAlert },
  { label: "收藏题", to: "/app/politics/question-favorites", icon: Star },
  { label: "答题记录", to: "/app/politics/history", icon: History },
];

const adminNav: NavItem[] = [
  { label: "管理概览", to: "/admin", icon: Gauge },
  { label: "用户管理", to: "/admin/users", icon: UsersRound },
  { label: "内容与导入", to: "/admin/content", icon: Database },
  { label: "系统设置", to: "/admin/settings", icon: Settings2 },
  { label: "审计日志", to: "/admin/audit", icon: ScrollText },
];

const subjectOptions = [
  { id: "english", label: "英语", detail: "考研英语一", icon: GraduationCap },
  { id: "politics", label: "政治", detail: "考研政治", icon: Landmark },
] as const;

interface SubjectResponse {
  subjects: Array<{ id: string; name: string; shortName: string; enabled: boolean; capabilities: string[] }>;
}

function BrandSubjectSwitcher({
  currentSubject,
  enabledSubjects,
  mobile = false,
}: {
  currentSubject: SupportedSubjectId;
  enabledSubjects: ReadonlySet<SupportedSubjectId>;
  mobile?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const currentOptionRef = useRef<HTMLButtonElement>(null);
  const current = subjectOptions.find((option) => option.id === currentSubject) ?? subjectOptions[0];

  useEffect(() => {
    if (!open) return;
    const closeFromOutside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeFromKeyboard = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("pointerdown", closeFromOutside);
    document.addEventListener("keydown", closeFromKeyboard);
    const focusFrame = window.requestAnimationFrame(() => currentOptionRef.current?.focus());
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("pointerdown", closeFromOutside);
      document.removeEventListener("keydown", closeFromKeyboard);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={mobile ? "brand-subject-wrap brand-subject-wrap--mobile" : "brand-subject-wrap"}>
      <button
        ref={triggerRef}
        className={"brand-subject-trigger" + (open ? " brand-subject-trigger--open" : "")}
        type="button"
        aria-label={`切换科目，当前：${current.detail}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="brand-subject-trigger__copy">
          <span className="brand__word">PrepTrack</span>
          <span className="brand-subject-trigger__meta"><span className="brand__rule" aria-hidden="true" /><span className="brand-subject-trigger__current" data-short-label={current.label}>{mobile ? current.label : current.detail}</span></span>
        </span>
        <ChevronDown className="brand-subject-trigger__chevron" strokeWidth={1.6} />
      </button>
      <div id={menuId} className={"subject-menu" + (open ? " subject-menu--open" : "")} role="menu" aria-label="选择学习科目" aria-hidden={!open}>
        <p className="subject-menu__label">切换科目</p>
        {subjectOptions.map((subject) => {
          const Icon = subject.icon;
          const selected = subject.id === currentSubject;
          const enabled = enabledSubjects.has(subject.id);
          return (
            <button
              ref={selected ? currentOptionRef : undefined}
              className={"subject-option" + (selected ? " subject-option--selected" : "")}
              type="button"
              role="menuitemradio"
              aria-checked={selected}
              disabled={!enabled}
              tabIndex={open && enabled ? 0 : -1}
              key={subject.id}
              onClick={() => {
                saveLastSubject(subject.id);
                setOpen(false);
                void navigate({ to: "/app/$subject/dashboard", params: { subject: subject.id } });
              }}
            >
              <span className="subject-option__icon"><Icon strokeWidth={1.6} /></span>
              <span className="subject-option__copy"><strong>{subject.label}</strong><small>{subject.detail}</small></span>
              {selected ? <Check className="subject-option__check" /> : !enabled ? <small className="subject-option__status">未开放</small> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function UserAvatar({ user, className = "avatar" }: { user: User; className?: string }) {
  const label = user.displayName ?? user.email;
  return <span className={className} aria-hidden="true">{user.avatarUrl ? <img src={user.avatarUrl} alt="" /> : label.slice(0, 1).toUpperCase()}</span>;
}

function NavigationLink({ item, compact = false }: { item: NavItem; compact?: boolean }) {
  const location = useLocation();
  const active = item.to === "/admin"
    ? location.pathname === "/admin" || location.pathname === "/admin/"
    : (item.activePaths ?? [item.to]).some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
  const Icon = item.icon;
  return (
    <Link to={item.to} className={active ? "nav-link nav-link--active" : "nav-link"} aria-current={active ? "page" : undefined} aria-label={compact ? item.label : undefined} title={item.label}>
      <Icon strokeWidth={1.7} />
      {compact ? <span className="sr-only">{item.label}</span> : <span>{item.label}</span>}
    </Link>
  );
}

function AccountMenu({ user, subject, onLogout }: { user: User; subject: SupportedSubjectId; onLogout: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => { if (event.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", close); document.removeEventListener("keydown", escape); };
  }, [open]);
  return (
    <div className="account-menu-wrap" ref={rootRef}>
      <button className="mobile-account" type="button" aria-haspopup="menu" aria-expanded={open} aria-label="打开账户菜单" onClick={() => setOpen((value) => !value)}>
        <UserAvatar user={user} className="mobile-avatar" />
      </button>
      {open ? (
        <div className="account-menu" role="menu">
          <Link to="/app/$subject/profile" params={{ subject }} role="menuitem" onClick={() => setOpen(false)}><UserRound /> 个人中心</Link>
          {user.roles.includes("ADMIN") ? <Link to="/admin" role="menuitem" onClick={() => setOpen(false)}><ShieldCheck /> 管理后台</Link> : null}
          <button type="button" role="menuitem" onClick={() => void onLogout()}><LogOut /> 退出登录</button>
        </div>
      ) : null}
    </div>
  );
}

function SidebarAccount({ user, subject, onLogout }: { user: User; subject: SupportedSubjectId; onLogout: () => Promise<void> }) {
  return (
    <div className="sidebar__account">
      <Link className="account-link" to="/app/$subject/profile" params={{ subject }} aria-label="打开个人中心">
        <UserAvatar user={user} />
        <span className="account-copy"><strong>{user.displayName ?? "学习者"}</strong><span>{user.email}</span></span>
      </Link>
      <button className="icon-button" onClick={() => void onLogout()} type="button" aria-label="退出登录" title="退出登录"><LogOut strokeWidth={1.6} /></button>
    </div>
  );
}

export function AppShell() {
  const { user, loading, logout: endSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams({ strict: false }) as { subject?: string };
  const currentSubject = isSupportedSubject(params.subject) ? params.subject : "english";
  const subjectsQuery = useQuery({
    queryKey: ["subjects"],
    queryFn: () => apiFetch<SubjectResponse>("/subjects"),
    staleTime: 5 * 60_000,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
    retry: false,
  });
  const enabledSubjects = useMemo(() => {
    return enabledSupportedSubjects(subjectsQuery.data?.subjects ?? []);
  }, [subjectsQuery.data?.subjects]);
  const availability = subjectAvailability(
    currentSubject,
    subjectsQuery.data?.subjects ?? [],
    subjectQueryState(Boolean(subjectsQuery.data), subjectsQuery.isError),
  );
  useEffect(() => { if (!loading && !user) void navigate({ to: "/auth/login", replace: true }); }, [loading, navigate, user]);
  useEffect(() => {
    if (!isSupportedSubject(params.subject)) {
      void navigate({ to: "/app/$subject/dashboard", params: { subject: "english" }, replace: true });
      return;
    }
    if (availability === "disabled") {
      void navigate({ to: "/app/$subject/dashboard", params: { subject: "english" }, replace: true });
      return;
    }
    const requiredCapability = requiredSubjectCapability(location.pathname);
    const manifest = subjectsQuery.data?.subjects.find((subject) => subject.id === params.subject);
    if (manifest && requiredCapability && !manifest.capabilities.includes(requiredCapability)) {
      void navigate({
        to: "/app/$subject/dashboard",
        params: { subject: requiredCapability === "dashboard" ? "english" : params.subject },
        replace: true,
      });
      return;
    }
    saveLastSubject(params.subject);
  }, [availability, location.pathname, navigate, params.subject, subjectsQuery.data?.subjects]);
  if (loading) return <div className="center-screen"><LoadingLine /></div>;
  if (!user) return null;
  const logout = async () => {
    await endSession();
    await navigate({ to: "/auth/login" });
  };
  const learningNav = currentSubject === "politics" ? politicsLearningNav : englishLearningNav;
  const desktopNav = user.roles.includes("ADMIN")
    ? [...learningNav, { label: "管理后台", to: "/admin", icon: ShieldCheck }]
    : learningNav;
  return (
    <div className="app-layout">
      <a href="#main-content" className="skip-link">跳到主要内容</a>
      <aside className="sidebar">
        <BrandSubjectSwitcher currentSubject={currentSubject} enabledSubjects={enabledSubjects} />
        <nav className="main-nav" aria-label="主导航">{desktopNav.map((item) => <NavigationLink key={item.to} item={item} />)}</nav>
        <SidebarAccount user={user} subject={currentSubject} onLogout={logout} />
      </aside>
      <header className="mobile-header"><BrandSubjectSwitcher currentSubject={currentSubject} enabledSubjects={enabledSubjects} mobile /><AccountMenu user={user} subject={currentSubject} onLogout={logout} /></header>
      <main id="main-content" className="app-main">
        <div className="route-view" key={location.href}>
          {availability === "ready" ? <Outlet /> : availability === "unavailable" ? (
            <div className="page">
              <EmptyState
                title="暂时无法确认科目状态"
                description="政治学习内容仍保持锁定，请重新连接后再试。"
                action={<Button type="button" variant="primary" onClick={() => void subjectsQuery.refetch()}>重新检查</Button>}
              />
            </div>
          ) : <div className="center-screen"><LoadingLine /></div>}
        </div>
      </main>
      <nav className="bottom-nav" aria-label="移动端导航">{learningNav.map((item) => <NavigationLink compact key={item.to} item={item} />)}</nav>
    </div>
  );
}

export function AdminShell() {
  const { user, loading, logout: endSession } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    if (!loading && (!user || !user.roles.includes("ADMIN"))) {
      if (user) void navigate({ to: "/app/$subject/dashboard", params: { subject: readLastSubject() }, replace: true });
      else void navigate({ to: "/auth/login", replace: true });
    }
  }, [loading, navigate, user]);
  if (loading || !user?.roles.includes("ADMIN")) return <div className="center-screen"><LoadingLine /></div>;
  const logout = async () => {
    await endSession();
    await navigate({ to: "/auth/login" });
  };
  return (
    <div className="admin-layout">
      <a href="#admin-main-content" className="skip-link">跳到主要内容</a>
      <aside className="admin-sidebar sidebar">
        <Link to="/admin" className="admin-brand"><span className="brand__word">PrepTrack</span><span className="brand__rule" /><small>管理后台</small></Link>
        <nav className="main-nav" aria-label="后台导航">{adminNav.map((item) => <NavigationLink key={item.to} item={item} />)}</nav>
        <Link to="/app/$subject/dashboard" params={{ subject: readLastSubject() }} className="back-to-study"><ArrowLeft /> 返回学习区</Link>
        <SidebarAccount user={user} subject={readLastSubject()} onLogout={logout} />
      </aside>
      <header className="mobile-header admin-mobile-header">
        <Link to="/app/$subject/dashboard" params={{ subject: readLastSubject() }} className="icon-button" aria-label="返回学习区"><ArrowLeft /></Link>
        <Link to="/admin" className="admin-mobile-brand"><strong>PrepTrack</strong><span>管理后台</span></Link>
        <AccountMenu user={user} subject={readLastSubject()} onLogout={logout} />
      </header>
      <main id="admin-main-content" className="admin-main"><div className="route-view" key={location.href}><Outlet /></div></main>
      <nav className="bottom-nav admin-bottom-nav" aria-label="移动端后台导航">{adminNav.map((item) => <NavigationLink compact key={item.to} item={item} />)}</nav>
    </div>
  );
}
